import BellIcon from 'url:../images/icons/bell.svg'
import BellIconDark from 'url:../images/icons/bell_dark.svg'

import {
  ADMIN_APPS,
  APPS,
  FEATURES,
  IDENTITY_ADMIN_APP,
  STATUS_MESSAGES,
} from './constants'

interface JWTPayload {
  [key: string]: any // eslint-disable-line
}

export function decodeJWT(token: string): JWTPayload {
  // Split the token into its parts
  const parts = token.split('.')

  // Ensure the token has three parts
  if (parts.length !== 3) {
    throw new Error('Invalid token')
  }

  // Decode the payload
  const payload = parts[1]
  const decodedPayload = JSON.parse(
    atob(payload.replace(/-/g, '+').replace(/_/g, '/')),
  )

  return decodedPayload
}

const COGNITO_GROUP_MAP: Record<string, string> = {
  catalogue: 'app:catalogue',
  catalogue_admin: 'app:catalogue_admin',
  identity: 'app:identity',
  identity_admin: 'app:identity_admin',
  pentest: 'app:pentest',
  pentest_admin: 'app:pentest_admin',
  sfe: 'app:sfe',
  sfe_admin: 'app:sfe_admin',
}

export function extractPermissions(jwt: Record<string, any>): string[] {
  const permissions = new Set<string>()

  // Auth0 permissions
  // Cognito custom permissions claim
  if (Array.isArray(jwt.permissions)) {
    jwt.permissions
      .filter((p: string) => p.startsWith('app:'))
      .forEach((p: string) => permissions.add(p))
  }

  // Cognito groups
  const groups = jwt['cognito:groups']

  if (Array.isArray(groups)) {
    groups
      .map((group: string) => COGNITO_GROUP_MAP[group])
      .filter(Boolean)
      .forEach((permission: string) => permissions.add(permission))
  }

  return [...permissions]
}

// Accepts common shapes from bundlers: string URL, { default: string }, etc.
export function resolveImageSrc(thumbnail: unknown): string {
  if (typeof thumbnail === 'string') return thumbnail
  if (thumbnail && typeof thumbnail === 'object') {
    const d = (thumbnail as { default?: unknown }).default
    if (typeof d === 'string') return d
  }
  return ''
}

async function buildCard(
  { title, description = '', thumbnail = '', href, app_id, notif_href },
  token,
) {
  const content = document.getElementById('content')
  if (!content) throw new Error('#content not found')

  const tile = document.createElement('div')
  tile.className =
    'box-bg rounded-lg shadow-lg overflow-hidden text-center w-[300px] h-[300px] flex flex-col items-center space-between cursor-pointer relative'
  tile.id = `tile_${app_id}`

  // Link
  const link = document.createElement('a')
  link.className = 'box flex flex-col items-center'
  link.href = href
  link.target = '_blank'
  link.rel = 'noopener noreferrer'

  // Title
  const cardTitle = document.createElement('h2')
  cardTitle.className = 'text-lg font-semibold p-4'
  cardTitle.textContent = title

  // Description
  const cardDescription = document.createElement('p')
  cardDescription.className = 'text-xs text-grey-50 mt-2 mb-4'
  cardDescription.textContent = description

  // Thumbnail
  const cardThumbnail = document.createElement('img')
  cardThumbnail.src = resolveImageSrc(thumbnail)
  cardThumbnail.className = 'w-60 h-60'
  cardThumbnail.alt = `${title} icon`

  link.append(cardTitle, cardDescription, cardThumbnail)

  // Bell (outside the <a>)
  const bell = document.createElement('div')
  bell.className = 'absolute right-0 top-0 p-3 cursor-not-allowed'
  bell.id = `bell_${app_id}`

  const bellIcon = document.createElement('img')
  bellIcon.src = BellIcon
  bellIcon.width = 20
  bellIcon.height = 20
  bellIcon.className = 'light-mode'

  const bellIconDark = document.createElement('img')
  bellIconDark.src = BellIconDark
  bellIconDark.width = 20
  bellIconDark.height = 20
  bellIconDark.className = 'dark-mode'

  bell.append(bellIcon, bellIconDark)

  tile.append(link, bell)
  content.appendChild(tile)

  let count = 0
  try {
    const response = await fetch(
      `${process.env.NOTIFICATION_SERVICE_URL}/v2/notification?app_id=${app_id}&read_value=unread`,
      {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      },
    )
    if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`)
    const notification_json = await response.json()
    count = notification_json.items?.length ?? 0
  } catch (error) {
    console.error('Error fetching notifications:', error)
  }

  if (count > 0) {
    const countEl = document.createElement('span')
    countEl.className =
      'w-5 h-5 rounded-full bg-red text-white font-medium absolute top-1 right-1 border-white border-2 text-[11px] flex justify-center items-center transition duration-200 hover:scale-125 hover:-translate-y-0.5'
    countEl.textContent = String(count)
    bell.appendChild(countEl)
    bell.classList.remove('cursor-not-allowed')
  }

  bell.addEventListener('click', (e) => {
    e.preventDefault()
    e.stopPropagation()
    if (count <= 0) return
    window.open(notif_href, '_blank', 'noopener,noreferrer')
  })
}

export async function displayApps(token?: string): Promise<void> {
  /* Decode JWT Token and extract apps */
  let permissions: string[]

  if (FEATURES.showOnlyEnabledApps) {
    permissions = extractPermissions(decodeJWT(token))
  } else {
    permissions = Object.keys(APPS)
  }

  document.getElementById('loader').classList.remove('hidden')

  /* Create a tile for each app and append it to content */

  permissions.forEach(async (element: string) => {
    if (APPS[element] !== undefined) {
      buildCard(APPS[element], token)
    }
  })

  document.getElementById('content').classList.remove('hidden')
  document.getElementById('loader').classList.add('hidden')

  if (permissions.includes(IDENTITY_ADMIN_APP)) {
    const userAdminLink = document.getElementById('user-admin-link')
    userAdminLink.setAttribute('href', ADMIN_APPS[IDENTITY_ADMIN_APP]['href'])
    userAdminLink.classList.remove('hidden')
    userAdminLink.classList.add('flex')
  }

  if (!permissions.length) {
    displayStatusMessage(STATUS_MESSAGES.insufficientPermissions.message)
  }
}

export function displayStatusMessage(message: string): void {
  document.getElementById('statusMessage').classList.remove('hidden')
  document.getElementById('loader').classList.add('hidden')
  document.getElementById('customMessage').innerHTML = message
}

export function applyTheme(darkMode: boolean) {
  document.documentElement.dataset.theme = darkMode ? 'dark' : ''
  if (darkMode) {
    document.getElementById('toggle-dot').classList.remove('-translate-x-2.5')
    document.getElementById('toggle-dot').classList.add('translate-x-2.5')
  } else {
    document.getElementById('toggle-dot').classList.remove('translate-x-2.5')
    document.getElementById('toggle-dot').classList.add('-translate-x-2.5')
  }
  setDarkMode(darkMode)
}

export function checkDarkMode() {
  if (typeof window !== 'undefined') {
    const saved = localStorage.getItem('darkMode')
    if (saved !== null) {
      return JSON.parse(saved)
    }
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  }
  return false
}

export function setDarkMode(darkMode: boolean) {
  localStorage.setItem('darkMode', JSON.stringify(darkMode))
}

export function setFavicon(): void {
  const link = document.createElement('link')
  link.rel = 'icon'
  link.type = 'image/x-icon'
  link.href = `${process.env.CSP_CDN}/favicon.ico`
  document.head.appendChild(link)
}
